<template>
  <section class="form-fw__content">
    <form
      v-if="status !== isFailed"
      class="form--custom"
      novalidate
      @submit.prevent="handleSubmit"
      autocomplete="off"
    >
      <fieldset
        class="form__container"
        :disabled="status === isLoading"
      >
        <header class="form__container__header">
          <h2 class="form__container__title">{{ $t(`${namespace}.sectionCompanyTitle`) }}</h2>
          <p class="form__container__desc" v-html="$t(`${namespace}.sectionCompanyText`)" />
        </header>

        <div class="form__container__content pb-0">
          <label>{{ $t(`${namespace}.fieldCompaniesLabel`) }}*</label>
          <div class="form__group">
            <transition name="fade">
              <AutoComplete
                v-if="!isFetching"
                kind="company"
                :data="companies"
                :placeholder="$t(`${namespace}.fieldCompaniesPlaceholder`)"
                :isValid="isCompanyValid"
                multiple
                required
                @input="updateCompany"
              />
            </transition>
          </div>
        </div>
      </fieldset>

      <fieldset
        class="form__container"
        :disabled="status === isLoading"
      >
        <header class="form__container__header">
          <h2 class="form__container__title">{{ $t(`${namespace}.sectionOneTitle`) }}</h2>
          <p class="form__container__desc" v-html="$t(`${namespace}.sectionOneText`)" />
        </header>

        <div class="form__container__content pb-0">
          <v-layout row wrap>
            <v-flex sm6 xs12>
              <div class="pr-2--desktop">
                <label>{{ $t(`${namespace}.fieldUserLabel`) }}*</label>
                <v-text-field
                  v-model="form.name"
                  :placeholder="$t(`${namespace}.fieldUserPlaceholder`)"
                  :error-messages="errors.collect($t(`${namespace}.fieldUserName`))"
                  v-validate="{
                    required: true,
                    min: 3,
                    max: 255
                  }"
                  :data-vv-name="$t(`${namespace}.fieldUserName`)"
                  required
                  flat
                  outline
                  solo
                  color="accent"
                />
              </div>
            </v-flex>

            <v-flex sm6 xs12>
              <div class="pl-2--desktop">
                <label>{{ $t(`${namespace}.fieldEmailLabel`) }}*</label>
                <v-text-field
                  v-model="form.email"
                  :placeholder="$t(`${namespace}.fieldEmailPlaceholder`)"
                  :error-messages="errors.collect($t(`${namespace}.fieldEmailName`))"
                  v-validate="{
                    required: true,
                    email: true
                  }"
                  :data-vv-name="$t(`${namespace}.fieldEmailName`)"
                  required
                  flat
                  outline
                  solo
                  :disabled="hasEmail"
                  color="accent"
                />
              </div>
            </v-flex>
          </v-layout>

          <div>

            <div @click="tooglePasswordVisibility($event, 'password')">
              <label for="">{{ $t(`${namespace}.fieldPasswordLabel`) }}</label>
              <v-text-field
                v-model="form.password"
                :type="passwordInputType ? 'password' : 'text'"
                name="password"
                :placeholder="$t(`${namespace}.fieldPasswordPlaceholder`)"
                :append-icon="passwordInputType ? 'remove_red_eye' : 'visibility_off'"
                title=""
                v-validate="{
                  hasValidPassword: true,
                  min: 8
                }"
                :error-messages="errors.collect($t(`${namespace}.fieldPasswordName`))"
                :data-vv-name="$t(`${namespace}.fieldPasswordName`)"
                ref="senha"
                flat
                outline
                solo
              />
            </div>

            <div @click="tooglePasswordVisibility($event, 'confirmPassword')">
              <label for="">{{ $t(`${namespace}.fieldConfirmPasswordLabel`) }}</label>
              <v-text-field
                v-model="passwordConfirm"
                :type="confirmPasswordInputType ? 'password' : 'text'"
                name="password"
                :placeholder="$t(`${namespace}.fieldConfirmPasswordPlaceholder`)"
                :append-icon="confirmPasswordInputType ? 'remove_red_eye' : 'visibility_off'"
                v-validate="form.password ? 'required|confirmed:senha' : ''"
                :error-messages="errors.collect($t(`${namespace}.fieldConfirmPasswordName`))"
                :data-vv-name="$t(`${namespace}.fieldConfirmPasswordName`)"
                flat
                outline
                solo
              />
            </div>

            <div>
              <label>{{ $t(`${namespace}.fieldPhoneLabel`) }}*</label>
              <v-text-field
                v-model="phone"
                type="tel"
                name="phone"
                :placeholder="$t(`${namespace}.fieldPhonePlaceholder`)"
                :error-messages="errors.collect($t(`${namespace}.fieldPhoneName`))"
                v-validate="{ required: true, regex: /^\(\d{2}\)\s(\d{4,5})-(\d{4})$/ }"
                :data-vv-name="$t(`${namespace}.fieldPhoneName`)"
                required
                flat
                outline
                solo
                color="accent"
                maxlength="15"
                @input="setPhoneMask($event, 'phone')"
              />
            </div>

            <div v-if="hasRole('user', 'ROLE_POST_VERIFY_METHOD')">
              <label>{{ $t(`${namespace}.loginVerifyType`) }}*</label>
              <v-select
                v-model="form.step2Verify"
                item-text="label"
                :placeholder="$t(`${namespace}.loginVerifyTypePlaceholder`)"
                :items="loginVerifyOptions"
                :error-messages="errors.collect($t(`${namespace}.loginVerifyType`))"
                v-validate="{ required: true }"
                :data-vv-name="$t(`${namespace}.loginVerifyType`)"
                noDataText=""
                required
                flat
                outline
                solo
                color="accent"
              />
            </div>

            <PasswordTips :password="form.password" />
          </div>
        </div>
      </fieldset>

      <fieldset
        class="form__container"
        :disabled="status === isLoading"
      >
        <header class="form__container__header">
          <h2 class="form__container__title">{{ $t(`${namespace}.sectionTwoTitle`) }}</h2>
          <p class="form__container__desc">{{ $t(`${namespace}.sectionTwoText`) }}</p>
        </header>

        <div class="form__container__content pt-1 pb-1">
          <div
            class="profiles"
            :class="[
              { 'profiles--error': !hasValidProfile },
              { 'profiles--admin': profilesList.length === 8 }
            ]"
          >
            <v-radio-group
              v-model="form.profile"
              v-validate="{ required: true }"
              :error-messages="errors.collect($t(`${namespace}.fieldProfileName`))"
              :data-vv-name="$t(`${namespace}.fieldProfileName`)"
              required
              class="my-0"
            >
              <label
                v-for="profile in profilesList"
                v-show="profile.visible"
                :key="profile.value"
                class="profile "
                :class="{ 'profile--active': form.profile === profile.value }"
              >
                <v-radio :value="profile.value" />
                <header class="profile__header">
                  <v-icon>{{ profile.icon }}</v-icon>
                  <h3 class="profile__title">{{ profile.name }}</h3>
                </header>
                <p class="profile__text">{{ profile.text }}</p>
              </label>
            </v-radio-group>
          </div>
        </div>
      </fieldset>

      <fieldset
        v-show="form.profile === 'CorporateUser' && reportsURLParam"
        class="form__container"
        :disabled="status === isLoading"
      >
        <header class="form__container__header">
          <h2 class="form__container__title">{{ $t(`${namespace}.sectionScheduledReportsTitle`) }}</h2>
          <p class="form__container__desc">{{ $t(`${namespace}.sectionScheduledReportsText`) }}</p>
        </header>

        <div class="form__container__content pt-3 pb-2">
          <v-checkbox v-model="form.scheduleReportPermission" class="pb-2 mt-2 mb-2 cursor--pointer" hide-details>
            <template slot="label">
              <label class="cursor--pointer">{{ $t(`${namespace}.fieldScheduledReportsPasswordLabel`) }}</label>
            </template>
          </v-checkbox>
        </div>
      </fieldset>
    </form>

    <div
      v-if="status === isFailed"
      class="text-xs-center mt-5"
    >
      <span>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          href="#"
          :title="$t('Common.tryAgain')"
          class="text--link accent--text"
          @click.prevent="$emit('tryAgain')"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </span>
    </div>
  </section>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { isObject, isArray, objectToArray, hasRole, goBack } from 'utils/functions'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import AutoComplete from 'components/AutoComplete'
import PasswordTips from 'components/PasswordTips'
import { masks } from 'utils/masks'
import { Omega, OmegaEventIds, OmegaBusiness, OmegaStatus } from 'common/omega'
export default {
  name: 'UserForm',
  props: {
    kind: {
      type: String
    },
    data: {
      type: Object
    },
    submit: {
      type: Boolean
    },
    status: {
      type: Number
    }
  },
  components: {
    AutoComplete,
    PasswordTips
  },
  computed: {
    ...mapState({
      globalCompany: (state) => {
        Omega.trackBusiness(
          OmegaEventIds.tech_b2bv3_other,
          {
            business: OmegaBusiness.user,
            type: 'defaultCompany',
            status: OmegaStatus.op,
            params: {
              company: state.company?.selectedCompany?.id
            }
          }
        )
        if (state.company?.selectedCompany?.id) {
          return { // 这里处理了globalCompany只有两种类型：null 或 { id: number, name: string }
            id: state.company.selectedCompany.id,
            name: state.company.selectedCompany.name
          }
        } else { return null }
      }
    }),
    isLoading: () => STATUS_IS_LOADING,
    isSuccess: () => STATUS_SUCCESS,
    isFailed: () => STATUS_FAILED,
    profilesList () {
      return hasRole('profiles', 'ROLE_CORPORATE_USER') ? objectToArray(this.$t(`${this.namespace}.corporateProfiles`)) : objectToArray(this.$t(`${this.namespace}.profiles`))
    },
    reportsURLParam () {
      return this.$route.query && this.$route.query.reports
    },
    loginVerifyOptions () {
      const loginVerifyAllOptions = objectToArray(this.$t(`${this.namespace}.loginVerifyAllOptions`))
      const step2VerifyMethod = this.$store.state.commonConfig.kvConfig.step2_verify_method?.split(',') || []
      return loginVerifyAllOptions.filter((option) => step2VerifyMethod.includes(option.value))
    }
  },
  data () {
    return {
      namespace: 'Views.UserPage.form',
      isFetching: false,
      companies: [],
      isCompanyValid: true,
      hasValidProfile: true,
      passwordInputType: true,
      confirmPasswordInputType: true,
      password: null,
      passwordConfirm: null,
      hasEmail: false,
      phone: null,
      form: {
        name: null,
        email: null,
        companyIds: [],
        profile: null,
        webCallPermission: false,
        scheduleReportPermission: false,
        benefitsPermission: false,
        password: null,
        phone: null,
        step2Verify: null
      }
    }
  },
  methods: {
    ...mapActions({
      successAlert: 'snackbarAlert/success',
      errorAlert: 'snackbarAlert/error',
      showAlert: 'snackbar/show'
    }),

    hasRole: (group, role) => hasRole(group, role),

    objectToArray: (val) => objectToArray(val),

    setPhoneMask (val, mask) {
      this.phone = masks[mask](val)
      this.form.phone = val.replace(/\D/g, '')
    },

    isValidObject: (val) => isObject(val),

    isValidArray: (val) => isArray(val),

    prevPage () {
      goBack()
    },

    setData () {
      try {
        if (this.kind === 'editar' && this.isValidObject(this.data)) {
          this.isFetching = true
          this.hasEmail = true

          const {
            companies,
            companyIds,
            name,
            email,
            phone,
            profile,
            webCallPermission,
            scheduleReportPermission,
            benefitsPermission,
            step2Verify
          } = this.data

          this.companies = companies.map(item => ({
            id: item.id,
            name: item.name
          }))

          this.form = {
            companyIds,
            name,
            email,
            phone: (phone || '').replace(/\D/g, ''),
            profile,
            webCallPermission,
            scheduleReportPermission,
            benefitsPermission,
            password: null,
            step2Verify
          }
          this.setPhoneMask(this.form.phone, 'phone')
          setTimeout(() => { this.isFetching = false }, 200)
        }

        if (this.kind !== 'editar' && this.globalCompany) {
          this.isFetching = true
          this.updateCompany([this.globalCompany])
          setTimeout(() => { this.isFetching = false }, 200)
        }
      } catch (e) {
        console.log(e)
      }
    },

    updateCompany (val) { // 这个val一定是[{ id: number, name: string }]
      if (this.isValidArray(val)) {
        this.companies = val.map(item => ({
          id: item.id,
          name: item.name
        }))

        this.form.companyIds = val.map(item => item.id)
        this.isCompanyValid = true
        return
      }

      this.companies = []
      this.form.companyIds = []
      this.isCompanyValid = false
    },

    errorHandler (err) {
      if (err && err.response && err.response.data && this.isValidObject(err.response.data.errors)) {
        const errors = err.response.data.errors
        const key = Object.keys(errors)

        if (this.isValidArray(errors[key])) {
          this.errorAlert(`${key}: ${errors[key][0]}`)
          return
        }

        this.kind === 'editar'
          ? this.errorAlert(this.$t(`${this.namespace}.editErrorMessage`))
          : this.errorAlert(this.$t(`${this.namespace}.createErrorMessage`))
        return
      }

      this.kind === 'editar'
        ? this.errorAlert(this.$t(`${this.namespace}.editErrorMessage`))
        : this.errorAlert(this.$t(`${this.namespace}.createErrorMessage`))
    },

    redirectToUsers () {
      this.$router.push({ name: 'Users' })
    },

    dispatchHeaderErrorMessage () {
      const { errors } = this.$validator
      const errorsLength = errors.items.length > 0
      this.$emit('headerErrorMessage', errorsLength)
    },

    async create () {
      this.$emit('setCurrentStatus', this.isLoading)

      try {
        let userData = { ...this.form }
        const { data: res } = await this.$apiService.users.create(userData, {
          sendWelcomeEmail: true
        })

        if (res && !res.valid) {
          scrollTo(0, 0)
          const message = this.isValidArray(res.reasons) ? res.reasons[0] : this.$t('Common.genericErrorMsg')
          this.errorAlert(message)
        } else {
          this.successAlert(this.$t(`${this.namespace}.createSuccessMessage`))
          this.redirectToUsers()
        }
      } catch (err) {
        this.errorHandler(err)
      }
      this.$emit('setCurrentStatus', this.isSuccess)
    },

    async edit () {
      this.$emit('setCurrentStatus', this.isLoading)

      try {
        const userId = this.data.corporateId
        let userData = { ...this.form }
        const { data: res } = await this.$apiService.users.update(userId, userData)

        if (res && !res.valid) {
          scrollTo(0, 0)
          const message = this.isValidArray(res.reasons) ? res.reasons[0] : this.$t('Common.genericErrorMsg')
          this.errorAlert(message)
        } else {
          this.successAlert(this.$t(`${this.namespace}.editSuccessMessage`))
          this.redirectToUsers()
        }
      } catch (err) {
        this.errorHandler(err)
      }
      this.$emit('setCurrentStatus', this.isSuccess)
    },

    async handleSubmit () {
      try {
        // 提交编辑员工
        Omega.trackBusiness(
          OmegaEventIds.tech_b2bv3_other,
          {
            business: OmegaBusiness.user,
            type: 'submit',
            status: OmegaStatus.op
          }
        )
        const isValid = await this.$validator.validateAll()
        const isValidCompanies = this.companies && this.companies.length > 0

        this.dispatchHeaderErrorMessage()

        if (!isValidCompanies) this.isCompanyValid = false
        if (!this.form.profile) this.hasValidProfile = false

        if (isValid && isValidCompanies) {
          this.kind === 'editar' ? this.edit() : this.create()
        } else {
          const target = this.$validator.errors.items[0]
          if (target) {
            const elem = this.$el.querySelector(`[data-vv-name="${target.field}"]`)
            elem.scrollIntoView(false)
            elem.focus()
          }
        }
      } catch (err) {
        // 提交编辑员工失败
        Omega.trackBusiness(
          OmegaEventIds.tech_b2bv3_other,
          {
            business: OmegaBusiness.user,
            type: 'submit',
            status: OmegaStatus.error,
            msg: err?.message,
            params: {
              form: {
                name: this.form?.name
              },
              kind: this.kind
            }
          }
        )
        this.kind === 'editar'
          ? this.errorAlert(this.$t(`${this.namespace}.editErrorMessage`))
          : this.errorAlert(this.$t(`${this.namespace}.createErrorMessage`))
      }
    },

    tooglePasswordVisibility (event, type) {
      if (event.target.classList.contains('v-icon')) {
        if (type === 'password') {
          this.passwordInputType = !this.passwordInputType
        }

        if (type === 'confirmPassword') {
          this.confirmPasswordInputType = !this.confirmPasswordInputType
        }
      }
    }
  },
  watch: {
    kind () {
      this.setData()
    },

    data: {
      deep: true,
      handler () {
        this.setData()
      }
    },

    submit () {
      this.handleSubmit()
    },

    'form.profile' (val) {
      this.hasValidProfile = val
    }
  },
  created () {
    this.setData()

    this.$validator.extend('hasValidPassword', {
      getMessage: field => this.$t(`${this.namespace}.passwordDefaultError`),
      validate: value => {
        function isValid (text) {
          const rules = {
            charsCount: text.length >= 8,
            lowerCase: /(?=.*[a-z])/.test(text),
            upperCase: /(?=.*[A-Z])/.test(text),
            number: /(?=.*[0-9])/.test(text),
            specialChars: /(?=.*[?!@#$%^&*()_\-+=`~[\]{}:;'<>/\\]+)/.test(text)
          }

          return rules.charsCount && rules.lowerCase && rules.upperCase && rules.number && rules.specialChars
        }

        return isValid(value)
      }
    })
  }
}
</script>

<style lang="scss">
.profiles {

  .v-input--radio-group--column .v-input--radio-group__input {
    flex-direction: row;
    flex: 1 0 auto;
    flex-wrap: wrap;

    @media (min-width: 992px) {
      margin-left: -2%;
    }
  }
}
</style>

<style lang="scss" scoped>
.form__group {
  min-height: 74px;
}

.profiles {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;

  &.profiles--error {

    .profile {
      border-color: #e20d0d;

      .v-icon,
      .profile__title,
      .profile__text {
        color: #e20d0d;
      }
    }
  }

  &.profiles--admin {

    .profile {

      @media (min-width: 1200px) {
        margin: 2% 0 0 2%;
        width: 23%;
      }
    }
  }
}

.profile {
  background-color: #fafafa;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  margin: 0 0 2%;
  overflow: hidden;
  padding: 15px 10px;
  position: relative;
  transition: background .26s, box-shadow .26s;
  user-select: none;
  width: 100%;

  @media (min-width: 992px) {
    margin: 2% 0 0 2%;
    width: 48%;
  }

  &:hover:not(.profile--active) {
    background-color: #fff;
    box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.2);
  }

  &.profile--active {
    background-color: #fffadd;
    border-color: #ffe856;
    cursor: default;

    .v-icon {
      color: $accent;
    }
  }

  .v-radio {
    left: -999em;
    opacity: 0;
    position: absolute;
    top: -999em;
    visibility: hidden;
  }

  .profile__header {
    display: flex;
    padding-bottom: 10px;
  }

  .v-icon {
    margin-right: 8px;
  }

  .profile__title {
    align-items: center;
    color: #4a4b4a;
    display: flex;
    font-family: 'Montserrat', sans-serif;
    font-size: 12px;
    font-weight: 800;
    line-height: 1;
  }

  .profile__text {
    color: #757575;
    font-size: 12px;
    margin-bottom: 0;
  }
}
</style>
